import type { MouseEvent } from "react";
import { useLocation } from "react-router-dom";
import type { IconName } from "@theorgchartdev/storybook";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { isCanvasRoute } from "@coreapp/constants";
import { useNavigationContext } from "@/providers/NavigationProvider";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import * as StateLib from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { enumFeaturesList, DISPLAY_MODES, SEARCH_PARAM_DISPLAY_KEY } from "@coreapp/constants";
import { CHART_QUERY_PARAM_KEYS } from "@/appComponents/routes";
import { useRouteState, type RouteState } from "@/hooks/useRouteState";
import type { SidebarTool } from "@/appComponents/routes";
import type { PopoverText } from "@/appComponents/Blocks/Composites/UpgradePopover/UpgradePopover";

export type FeatureKey = (typeof enumFeaturesList)[keyof typeof enumFeaturesList];

export const getPlanningUpgradePopoverText = (): PopoverText => ({
  message: appstr("planning upgrade message"),
  template: appstr("planning upgrade template"),
  formatConfig: {
    bold1: { type: "bold" as const }
  },
  replaceDict: {
    bold1: appstr("Looking to create a strategic future plan?")
  }
});

/** A single main-nav item (Chart/Directory/Planning/PIT/Reports). `key` is a stable,
 * locale-independent identifier — unlike `name`, which is the translated display label
 * and must never be used as a lookup key. */
export type MainNavItemOption = {
  key: string;
  name: string;
  icon: IconName;
  value: number;
  route: string;
  checked: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: (e: MouseEvent) => void;
  showUpgrade?: boolean;
  featureKey?: FeatureKey;
  upgradePopoverText?: PopoverText;
};

export type UseMainNavItemsResult = {
  options: MainNavItemOption[];
  namedOptions: Record<string, MainNavItemOption>;
  handleMainNavClick: (targetDisplay: string | null) => void;
};

const isCanvasResource = (r: RouteState) => r.resourceType === "base" || r.resourceType === "open";

// For the Settings submenu, see useSettingsMenuOptions instead.
export const useMainNavItems = (): UseMainNavItemsResult => {
  const {
    canAccessReporting,
    canAccessDirectory,
    showPricingAndPackaging,
    shouldShowPlanning,
    shouldShowPIT
  } = usePermissionCheck();
  const route = useRouteState();
  const setRouteParam = route.setParam;
  const nav = useAppNavigate();
  const location = useLocation();
  const {
    chartExplorer: { openChartExplorer },
    planning: { PrepEnterPlanning }
  } = useNavigationContext();
  const wsMgr = StateLib.GetWSMgr();
  const { chartDocument } = useChartDocumentContext();
  const hasDoc = chartDocument?.state != null;

  const handleMainNavClick = (targetDisplay: string | null) => {
    if (!isCanvasRoute(location.pathname)) {
      // Non-canvas route (settings, etc.): read canvas context params
      // preserved in the settings URL and navigate home with them.
      const searchParams = new URLSearchParams(location.search);
      const params: Record<string, string> = {};
      for (const key of CHART_QUERY_PARAM_KEYS) {
        const val = searchParams.get(key);
        if (val !== null) params[key] = val;
      }
      if (targetDisplay) {
        params[SEARCH_PARAM_DISPLAY_KEY] = targetDisplay;
      } else {
        delete params[SEARCH_PARAM_DISPLAY_KEY];
      }
      nav.goHome(Object.keys(params).length > 0 ? params : undefined);
    } else if (targetDisplay) {
      setRouteParam(SEARCH_PARAM_DISPLAY_KEY, targetDisplay);
    } else {
      setRouteParam(SEARCH_PARAM_DISPLAY_KEY, null);
    }
  };

  const resolveDefaultChartId = (): string | null => {
    //mirror FinishLogin fallback
    const allowed = wsMgr?.GetAllowedCharts?.() ?? [];
    if (allowed.length === 0) return null;
    // `defaultchart` is assigned dynamically in QXWSMgr's metadata loader, not in
    // QOrgInfo's constructor, so it's invisible to TS's structural inference here.
    const orgDefault = (wsMgr?.GetOrgInfo?.() as { defaultchart?: unknown } | undefined)
      ?.defaultchart;
    const id = (allowed.find((c: any) => String(c?.["ID"]) === String(orgDefault)) ?? allowed[0])?.[
      "ID"
    ];
    return id != null ? String(id) : null;
  };

  const onChartNavClick = (e: MouseEvent) => {
    e.preventDefault();
    // No doc loaded - resolve to default chart so Chart button always attempts a recovery
    if (!StateLib.GetDoc()) {
      const fallbackId = resolveDefaultChartId();
      if (fallbackId != null) nav.toChart(fallbackId);
      return;
    }

    if (!isCanvasRoute(location.pathname)) {
      handleMainNavClick(null);
      return;
    }

    const searchParams = new URLSearchParams(location.search);

    const display = searchParams.get(SEARCH_PARAM_DISPLAY_KEY);
    if (display) {
      setRouteParam(SEARCH_PARAM_DISPLAY_KEY, null);
      return;
    }

    const inCreateFlow = searchParams.has("create");
    if (route.resourceType === "base" && !inCreateFlow) return;

    const chartId = StateLib.GetDoc()?.GetChartID() ?? route.chartId;
    if (chartId) {
      const all = nav.getCanvasContextParams();
      // getCanvasContextParams() carries whatever the URL currently has under these
      // keys — the app itself only ever writes valid SidebarTool values there.
      nav.toChart(String(chartId), { tool: all.tool as SidebarTool, zoom: all.zoom });
    } else {
      nav.goHome();
    }
  };

  const onPlanNavClick = (e: MouseEvent) => {
    PrepEnterPlanning();
    e.preventDefault();
  };

  const onPITClick = (e: MouseEvent) => {
    openChartExplorer(4);
    e.preventDefault();
  };

  const onReportNavClick = (e: MouseEvent) => {
    e.preventDefault();
    nav.setMode("reports");
  };

  const displayParam = new URLSearchParams(location.search).get(SEARCH_PARAM_DISPLAY_KEY);
  const resolveChecked = (isActive: (r: RouteState, display: string | null) => boolean): boolean =>
    isCanvasRoute(location.pathname) ? isActive(route, displayParam) : false;

  const getNavOptions = (): MainNavItemOption[] => {
    const options: MainNavItemOption[] = [
      {
        key: "chart",
        name: appstr("Chart"),
        icon: "sitemap",
        value: 1,
        route: "/",
        checked: resolveChecked(
          (r, display) => isCanvasResource(r) && r.mode !== "reports" && !display
        ),
        onClick: e => onChartNavClick(e)
      }
    ];
    if (canAccessDirectory) {
      options.push({
        key: "directory",
        name: appstr("Directory"),
        value: 2,
        icon: "address-book",
        route: "/directory",
        disabled: !hasDoc,
        checked: resolveChecked(
          (r, display) => isCanvasResource(r) && display === DISPLAY_MODES.DIRECTORY
        ),
        onClick: e => {
          e.preventDefault();
          if (!hasDoc) return;
          handleMainNavClick(DISPLAY_MODES.DIRECTORY);
        }
      });
    }

    if (shouldShowPlanning) {
      // Determine if we should show the upgrade badge
      const showPlanningSparkle =
        !getFeatureAccess(enumFeaturesList.planning) && showPricingAndPackaging;

      options.push({
        key: "planning",
        name: appstr("Planning"),
        value: 3,
        icon: "planning",
        route: "/planning",
        checked: resolveChecked(r => r.resourceType === "plan"),
        onClick: e => onPlanNavClick(e),
        showUpgrade: showPlanningSparkle,
        featureKey: enumFeaturesList.planning,
        upgradePopoverText: getPlanningUpgradePopoverText()
      });
    }

    if (shouldShowPIT) {
      const showPITUpgradeSparkle =
        !getFeatureAccess(enumFeaturesList.PIT) && showPricingAndPackaging;
      options.push({
        key: "pit",
        name: appstr("Point in time"),
        value: 4,
        icon: "clock-rotate-left",
        route: "/pit",
        checked: resolveChecked(() => false),
        onClick: e => onPITClick(e),
        showUpgrade: showPITUpgradeSparkle,
        featureKey: enumFeaturesList.PIT,
        upgradePopoverText: {
          message: appstr("pit archive message"),
          template: appstr("pit archive template"),
          formatConfig: {
            bold1: { type: "bold" as const }
          },
          replaceDict: {
            bold1: appstr("securely captured")
          }
        }
      });
    }

    if (canAccessReporting) {
      options.push({
        key: "reports",
        name: appstr("Reports"),
        value: 5,
        icon: "reporting",
        route: "/reports",
        checked: resolveChecked(r => isCanvasResource(r) && r.mode === "reports"),
        onClick: e => onReportNavClick(e)
      });
    }

    return options;
  };
  const options = getNavOptions();
  const namedOptions: Record<string, MainNavItemOption> = options.reduce(
    (acc, opt) => {
      acc[opt.key] = opt;
      return acc;
    },
    {} as Record<string, MainNavItemOption>
  );

  return {
    options,
    namedOptions,
    handleMainNavClick
  };
};
